/**
 * Server avatar error codes → user-facing message keys (contract §4.7).
 *
 * The server codes are the contract; the client owns the wording. Kept as a
 * table rather than inline strings so the set stays checkable against §4.7 and
 * a new code added upstream shows up as a miss here instead of as silence.
 *
 * An unknown code degrades to the generic message — a new server code must
 * never render as a blank toast or as a raw identifier.
 */
import i18n from '@/i18n';
const AVATAR_ERROR_KEYS: Record<string, string> = {
  avatar_unsupported_type: 'lib2.avatar.unsupportedType',
  avatar_format_mismatch: 'lib2.avatar.formatMismatch',
  avatar_file_too_large: 'lib2.avatar.fileTooLarge',
  avatar_image_too_large: 'lib2.avatar.imageTooLarge',
  avatar_invalid_image: 'lib2.avatar.invalidImage',
  avatar_rate_limited: 'lib2.avatar.rateLimited',
  // Ours to fix, not the user's: a misconfigured secret or a stale platformId.
  // They get "try later" rather than an internal detail they cannot act on.
  avatar_service_auth_error: 'lib2.avatar.serviceUnavailable',
  avatar_user_not_found: 'lib2.avatar.serviceUnavailable',
  avatar_service_unavailable: 'lib2.avatar.serviceUnavailable',
  missing_field_avatar: 'lib2.avatar.missingFile',
  // The avatar is the platform profile's: it is changed or removed there.
  avatar_managed_by_platform: 'lib2.avatar.managedByPlatform',
};

/** Pull the server's error code out of a thrown API error, if there is one. */
function codeOf(err: unknown): string | null {
  const body = (err as { body?: { error?: unknown } } | null)?.body;
  return typeof body?.error === 'string' ? body.error : null;
}

export function avatarErrorMessage(err: unknown): string {
  const code = codeOf(err);
  const key = code ? AVATAR_ERROR_KEYS[code] : undefined;
  return i18n.t(key ?? 'lib2.avatar.failed');
}

export const AVATAR_ERROR_CODES = Object.keys(AVATAR_ERROR_KEYS);
